import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import {TextStyle} from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align' //https://tiptap.dev/docs/examples/basics/formatting
import { EditorProvider,EditorContent, useCurrentEditor, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FontFamily } from '../../../utils/FontFamily'
import { FontSize } from '../../../utils/FontSize';
import Image from '@tiptap/extension-image'//https://tiptap.dev/docs/examples/basics/images
import {Table} from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'//https://tiptap.dev/docs/examples/basics/tables
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TaskItem from '@tiptap/extension-task-item'//https://tiptap.dev/docs/examples/basics/tasks
import TaskList from '@tiptap/extension-task-list'
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { faBold,faItalic,faLightbulb,faStrikethrough,faUnderline,faHeader,faListOl,faListUl,faUndo,faRepeat,faParagraph,faTable,faSubscript,faFont,faLink,faSuperscript, faAlignLeft, faAlignCenter, faAlignRight, faCode,faPaintBrush,faBan,faMinusCircle, faChainBroken, faMinus, faRotateRight, faDownload, faCaretDown, faImage, faCheckSquare, faTextHeight } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState } from 'react'
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { SengiColors } from '../../../utils/SengiColors'

interface MenuBarProps {
  editorIN: Editor | null;
};

//https://tiptap.dev/docs/examples/basics/default-text-editor
export const MenuBarRichTextEditor= ({editorIN}: MenuBarProps) => {
  //DEFINITIONS
  const editorContext = useCurrentEditor();
  const editor: Editor | null = editorIN ?? editorContext.editor;

  const [fontSize, setFontSize] = useState(12);
  const [fontColor,setFontColor] = useState('#958DF1');
  const [fileFormat, setFileFormat] = useState('');
  const fileFormatList = ['docx','html','json'];
  const fontList = ['Arial','Georgia','Courier New','Comic Sans MS',
  'Calibri','Times New Roman','Verdana','Open-Sans-Regular','Roboto-Regular','Roboto-Italic','RobotoCondensed-Regular','RobotoCondensed-Bold','RobotoCondensed-BoldItalic','RobotoCondensed-Italic','RobotoCondensed-Light','RobotoCondensed-LightItalic', ];
  const [currentFontFamily, setCurrentFontFamily] = useState('Open-Sans-Regular');
  const getCurrentFontFamily = () => currentFontFamily;

  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  //Font color
  const handleColorChange = (color: string) => {
    if(!editor){
      return;
    }
    setFontColor(color);
    editor.chain().focus().setColor(color).run();
  };
  //Font size
  const handleSetFontSize = () => {
    if (editor && fontSize) {
      editor.chain().focus().setFontSize(`${fontSize}px`).run();
    }
  };

  const handleInputChangeFontSize = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      editor?.chain().focus().setFontSize(`${value}px`).run();
      setFontSize(value);
    }
  };

  //Image insert
  const handleClickUploadImage = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

  const reader = new FileReader();
    reader.onload = () => {
      if (reader.result && typeof reader.result === 'string') {
        editor.chain().focus().setImage({ src: reader.result }).run();
      }
    };
    reader.readAsDataURL(file);
  };

  //Task List items for .docx
  const convertTaskListToStatic = (html: string) => {
  return html
    .replace(
      /<li[^>]*data-checked="(true|false)"[^>]*>\s*<label[^>]*>[\s\S]*?<\/label>\s*<div>\s*<p>(.*?)<\/p>\s*<\/div>\s*<\/li>/g,
      (_match, checked, content) => {
        const symbol = checked === 'true' ? '☑' : '☐';
        return `<div>${symbol} ${content}</div>`;
      }
    );
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  //export data
  useEffect(() => {
    const exportData = async () => {
      if (!editor) {
        return;
      }
      const filename = `${uuidv4()}.${fileFormat}`;
      let blob: Blob;

      switch (fileFormat) {
        case 'json': {
          const json = editor?.getJSON();
          blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
          saveAs(blob, filename);
          break;
        }
        case 'html': {
          const html = editor.getHTML();
          blob = new Blob([html], { type: 'text/html' });
          saveAs(blob, filename);
          break;
        }
        case 'docx': {
          const fontFamily = getCurrentFontFamily();
          let htmlContent = convertTaskListToStatic(editor.getHTML());
          
          const html = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office'
                  xmlns:w='urn:schemas-microsoft-com:office:word'
                  xmlns='http://www.w3.org/TR/REC-html40'>
              <head>
                <meta charset='utf-8'>
                <title>Export</title>
                <style>
                  body, p, span, div {
                    font-family: ${fontFamily} !important;
                  }
                  h1, h2, h3, h4, h5, h6 {
                    font-family: ${fontFamily} !important;
                  }
                </style>
              </head>
              <body style="font-family: ${fontFamily} !important;">${htmlContent}</body>
            </html>
          `;
          const blob = new Blob(['\ufeff', html], {
            type: 'application/msword',
          });
          saveAs(blob, filename);
          break;
        }
      }
      setFileFormat('');
    };

    if (fileFormat && fileFormatList.includes(fileFormat)) {
      exportData();
    }
  }, [fileFormat, editor]);

  if (!editor) {
    return (<></>);
  }
  return (
    <div className="control-group sticky-top p-2 z-index-sticky mb-2 rtx-menubar">
      <div className='row mb-1'>
        <div className='col-10'>
          <div className="d-flex flex-wrap align-items-center gap-0">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleBold()
                  .run()
              }
              className=  {`btn btn-dark border-success ${editor.isActive('bold') ? 'is-active' : ''}`}
              title='Bold'
            >
              <FontAwesomeIcon
                icon={faBold}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleItalic()
                  .run()
              }
              className={`btn btn-dark border-success ms-1 ${editor.isActive('italic') ? 'is-active' : ''}`}
              title='Italic'
            >
              <FontAwesomeIcon
                icon={faItalic}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleItalic()
                  .run()
              }
              className={`btn btn-dark border-success ms-1 ${editor?.isActive('underline') ? 'is-active' : ''}`}
              title="Underline"
            >
              <FontAwesomeIcon
                icon={faUnderline}
                size="1x"
              />
            </button>
            <button 
              onClick={() => editor.chain().focus().toggleHighlight().run()} 
              className={`btn btn-dark border-success ms-1 ${editor.isActive('highlight') ? 'is-active' : ''}`}
              title='Highlight'
            >
              <FontAwesomeIcon
                icon={faLightbulb}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn btn-dark border-success ms-1 ${editor.isActive('strike') ? 'is-active' : ''}`}
              title='Strike'
            >
              <FontAwesomeIcon
                icon={faStrikethrough}
                size="1x"
              />
            </button>
            <div className="dropdown">
              <button 
                className="btn btn-dark border-success ms-1" 
                type="button" 
                id="dropdownHeader"
                data-bs-toggle="dropdown"
                aria-haspopup="true" 
                aria-expanded="false"
              >
                <FontAwesomeIcon
                  icon={faHeader}
                  size="lg"
                  title="Header"
                />
              </button>
              <ul className="dropdown-menu dropdown-menu-dark ms-4 p-1 body-background border-dark rounded" aria-labelledby="dropdownHeader" style={{textAlignLast:'start'}}>
                <li className={`dropdown-item text-light p-2 fw-bold ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                  H1
                </li>
                <li className={`dropdown-item text-light p-2 fw-bold ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                  H2
                </li>
                <li className={`dropdown-item text-light p-2 fw-bold ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                  H3
                </li>
                <li className={`dropdown-item text-light p-2 fw-bold ${editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}>
                  H4
                </li>
                <li className={`dropdown-item text-light p-2 fw-bold ${editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}>
                  H5
                </li>
                <li className={`dropdown-item text-light p-2 fw-bold ${editor.isActive('heading', { level: 6 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}>
                  H6
                </li>
              </ul>
            </div>

            <div className="btn-group ms-3">
              <button
                type="button"
                className="btn btn-dark border-success dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                title="Font"
              >
                <FontAwesomeIcon icon={faFont} size="1x" />
              </button>
              <ul className="dropdown-menu dropdown-menu-dark">
                {fontList.map(font => (
                  <li key={font}>
                    <button
                      className="dropdown-item"
                      style={{ fontFamily: font }}
                      onClick={() => {
                          setCurrentFontFamily(font);

                          editor
                          .chain()
                          .focus()
                          .setTextSelection({ from: 0, to: editor.state.doc.content.size })
                          .setFontFamily(font)
                          .run()
                        }
                      }
                    >
                      {font}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ position: 'relative', display: 'inline-block' }} className='border border-success rounded ms-1 bg-dark'>
              <button
                onClick={handleSetFontSize}
                disabled={
                  !editor.can()
                    .chain()
                    .focus()
                    .toggleStrike()
                    .run()
                }
                className="btn btn-dark bg-transparent border-0 ms-1"
                title="Font size"
              >
                <FontAwesomeIcon
                  icon={faTextHeight}
                  className="border-end border-secondary pe-2"
                  size="1x"
                />
              </button>

              <input
                type="number"
                min={8}
                max={96}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                onBlur={() => {
                  editor
                    .chain()
                    .focus()
                    .setFontSize(`${fontSize}px`)
                    .run();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSetFontSize();
                    e.currentTarget.blur();
                  }
                }}
                title="Font Size"
                className="bg-transparent text-light border-0"
                style={{
                  width: '50px',
                  padding: '2px 4px',
                  marginLeft: '0.5rem',
                  appearance: 'textfield',
                  outline: 'none',
                }}
              />
            </div>

            
            <button
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn btn-dark border-success ms-1`}
              title='Superscript'
            >
              <FontAwesomeIcon
                icon={faSuperscript}
                size="1x"
              />
            </button>
            <button
              onClick={() =>editor.chain().focus().toggleSubscript().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn btn-dark border-success ms-1`}
              title='Subscript'
            >
              <FontAwesomeIcon
                icon={faSubscript}
                size="1x"
              />
            </button>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                ref={buttonRef}
                onClick={() => {
                  handleColorChange(fontColor);
                }}
                className={`btn btn-dark border-success ms-1 align-items-center gap-2 ${editor.isActive('textStyle', { color: fontColor }) ? 'is-active' : ''}`}
                title="Select Color"
              >
                <FontAwesomeIcon 
                  icon={faPaintBrush} 
                  className='border-end border-secondary pe-2'
                />
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: fontColor,
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  }}
                  className='ms-2'
                  onClick={() => setShowPicker(!showPicker)}
                  title='Choose Color'
                />
                <FontAwesomeIcon 
                  icon={faCaretDown} 
                  onClick={() => setShowPicker(!showPicker)}
                  title='Choose Color'
                  className='ms-1'
                />
              </button>

              {showPicker && (
                <div
                  ref={pickerRef}
                  style={{
                    position: 'absolute',
                    top: '110%',
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: SengiColors.sng_white,
                    border: `1px solid ${SengiColors.sng_pearl}`,
                    borderRadius: '4px',
                    padding: '8px',
                    minWidth: '140px',
                    boxShadow: `0px 4px 10px rgba(${SengiColors.sng_black} , 0.1)`,
                  }}
                >
                  <input
                    type="color"
                    value={fontColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    style={{ width: '100%', height: '40px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                  />
                </div>
              )}
            </div>

            
            <button 
              onClick={() => editor.chain().focus().setTextAlign('left').run()} 
              className={`btn btn-dark border-success ms-3 ${editor.isActive('left') ? 'is-active' : ''}`}
              title='Left'
            >
              <FontAwesomeIcon
                icon={faAlignLeft}
                size="1x"
              />
            </button>
            <button 
              onClick={() => editor.chain().focus().setTextAlign('center').run()} 
              className={`btn btn-dark border-success ms-1 ${editor.isActive('center') ? 'is-active' : ''}`}
              title='Center'
            >
              <FontAwesomeIcon
                icon={faAlignCenter}
                size="1x"
              />
            </button>
            <button 
              onClick={() => editor.chain().focus().setTextAlign('right').run()} 
              className={`btn btn-dark border-success ms-1 ${editor.isActive('right') ? 'is-active' : ''}`}
              title='Right'
            >
              <FontAwesomeIcon
                icon={faAlignRight}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`btn btn-dark border-success ms-1 ${editor.isActive('bulletList') ? 'is-active' : ''}`}
              title='Unordered list'
            >
              <FontAwesomeIcon
                icon={faListUl}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`btn btn-dark border-success ms-1 ${editor.isActive('orderedList') ? 'is-active' : ''}`}
              title='Ordered list'
            >
              <FontAwesomeIcon
                icon={faListOl}
                size="1x"
              />
            </button>

            <button
              onClick={() => alert('Function not implemented')}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn btn-dark border-danger ms-3 `}
              title='Tables'
            >
              <FontAwesomeIcon
                icon={faTable}
                size="1x"
              />
            </button>
            <button
              onClick={handleClickUploadImage}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn btn-dark border-success ms-1 `}
              title='Image'
            >
              <FontAwesomeIcon
                icon={faImage}
                size="1x"
              />
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }} 
            />
            <button
              onClick={() => editor?.chain().focus().toggleTaskList().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn btn-dark border-success ms-1 `}
              title='Tasks'
            >
              <FontAwesomeIcon
                icon={faCheckSquare}
                size="1x"
              />
            </button>
          
            <button
              onClick={() => editor.chain().focus().toggleCode().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleCode()
                  .run()
              }
              className={`btn btn-dark border-success ms-3 ${editor.isActive('code') ? 'is-active' : ''}`}
              title='Code'
            >
              <FontAwesomeIcon
                icon={faCode}
                size="1x"
              />
            </button>
            <button 
              onClick={() => editor.chain().focus().unsetAllMarks().run()}
              className={`btn btn-dark border-success ms-1 `}
              title='Clear marks'
            >
              <FontAwesomeIcon
                icon={faMinusCircle}
                size="1x"
              />
            </button>
            <button 
              onClick={() => editor.chain().focus().clearNodes().run()}
              className={`btn btn-dark border-success ms-1 `}
              title='Clear nodes'
            >
              <FontAwesomeIcon
                icon={faBan}
                size="1x"
              />
            </button>
            
            {/* <button
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              className={editor.isActive('codeBlock') ? 'is-active' : ''}
            >
              Code block
            </button>
            <button
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={editor.isActive('blockquote') ? 'is-active' : ''}
            >
              Blockquote
            </button> */}

            <button 
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              className={`btn btn-dark border-success ms-3`}
              title='Horizontal rule'
            >
              <FontAwesomeIcon
                icon={faMinus}
                size="1x"
              />
            </button>
            <button 
              onClick={() => editor.chain().focus().setHardBreak().run()}
              className={`btn btn-dark border-success ms-1`}
              title='Hard break'
            >
              <FontAwesomeIcon
                icon={faChainBroken}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor.chain().focus().setParagraph().run()}
              className={`btn btn-dark border-success ms-1 ${editor.isActive('paragraph') ? 'is-active' : ''}`}
              title='Paragraph'
            >
              <FontAwesomeIcon
                icon={faParagraph}
                size="1x"
              />
            </button>
          </div>
        </div>
        <div className='col-1'>
          <div className="button-group">
            <button
              onClick={() => editor.chain().focus().undo().run()}
              className='btn btn-dark border-success ms-1'
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .undo()
                  .run()
              }
              title='Undo'
            >
              <FontAwesomeIcon
                icon={faUndo}
                size="1x"
              />
            </button>
            <button
              onClick={() => editor.chain().focus().redo().run()}
              className='btn btn-dark border-success ms-1'
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .redo()
                  .run()
              }
              title='Redo'
            >
              <FontAwesomeIcon
                icon={faRotateRight}
                size="1x"
              />
            </button>
          </div>
        </div>
        <div className='col-1'>
          <div className="dropdown">
            <button 
              className="btn btn-chat bg-transparent text-secondary border-0" 
              type="button" 
              id="dropdownFileDownload"
              data-bs-toggle="dropdown"
              aria-haspopup="true" 
              aria-expanded="false"
            >
              <FontAwesomeIcon
                icon={faDownload}
                size="lg"
                title="Export"
              />
            </button>
            <ul className="dropdown-menu dropdown-menu-dark ms-4 p-1 body-background border-dark rounded" aria-labelledby="dropdownFileDownload" style={{textAlignLast:'start'}}>
              <li className="dropdown-item text-light p-2 fw-bold" onClick={e => setFileFormat('docx')}>
                Docx
              </li>
              <li className="dropdown-item text-light p-2 fw-bold" onClick={e => setFileFormat('html')}>
                HTML
              </li>
              <li className="dropdown-item text-light p-2 fw-bold" onClick={e => setFileFormat('json')}>
                JSON
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export const extensionsRichTextEditor = [
  Image,
  TextStyle,
  Highlight,
  Underline,
  FontFamily,
  FontSize,
  Subscript,
  Superscript,
  Color.configure({ types: ['textStyle', 'listItem', 'heading', 'paragraph'] }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  TaskList,
  TaskItem.configure({
    nested: true, //allows nested tasks
  }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
]

const content = `
<h2>
  Hi there,
</h2>
<p>
  this is a <em>basic</em> example of <strong>Tiptap</strong>. Sure, there are all kind of basic text styles you’d probably expect from a text editor. But wait until you see the lists:
</p>
<ul>
  <li>
    That’s a bullet list with one …
  </li>
  <li>
    … or two list items.
  </li>
</ul>
<p>
  Isn’t that great? And all of that is editable. But wait, there’s more. Let’s try a code block:
</p>
<pre><code class="language-css">body {
  display: none;
}</code></pre>
<p>
  I know, I know, this is impressive. It’s only the tip of the iceberg though. Give it a try and click a little bit around. Don’t forget to check the other examples too.
</p>
<blockquote>
  Wow, that’s amazing. Good work, boy! 👏
  <br />
  — Mom
</blockquote>
`

export default () => {
  return (
    <EditorProvider slotBefore={<MenuBarRichTextEditor editorIN={null}/>} extensions={extensionsRichTextEditor} content={content}>
    </EditorProvider>
  )
}